// Profile page component for user account management

import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { TestResult } from '../types';
import { usersAPI } from '../services/api';

const Profile: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const [testHistory, setTestHistory] = useState<TestResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    // Only load test history if user is active
    if (user && user.is_active) {
      loadTestHistory();
    } else {
      setLoading(false);
    }
  }, [user]);

  const loadTestHistory = async () => {
    try {
      const history = await usersAPI.getTestHistory();
      setTestHistory(history);
    } catch (err: any) {
      setError('Failed to load test history');
    } finally {
      setLoading(false);
    }
  };


  const getLevelColor = (level: string) => {
    const colors: { [key: string]: string } = {
      A1: 'bg-red-100 text-red-800',
      A2: 'bg-orange-100 text-orange-800',
      B1: 'bg-yellow-100 text-yellow-800',
      B2: 'bg-blue-100 text-blue-800',
      C1: 'bg-green-100 text-green-800',
      C2: 'bg-purple-100 text-purple-800',
    };
    return colors[level] || 'bg-gray-100 text-gray-800';
  };

  if (!user) {
    return (
      <div className="max-w-2xl mx-auto text-center">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Profile Not Found</h1>
        <p className="text-gray-600">Please log in to view your profile.</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">Profile</h1>

      {/* User Information */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Account Information</h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <p className="text-gray-900">{user.email}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
            <p className="text-gray-900">{user.full_name || 'Not provided'}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Account Status</label>
            <span
              className={`px-2 py-1 rounded text-sm font-medium ${
                user.is_active
                  ? 'bg-green-100 text-green-800'
                  : 'bg-yellow-100 text-yellow-800'
              }`}
            >
              {user.is_active ? 'Active' : 'Inactive'}
            </span>
            {!user.is_active && (
              <p className="text-sm text-gray-500 mt-1">
                Your account has been deactivated. Contact an administrator for assistance.
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Member Since</label>
            <p className="text-gray-900">
              {new Date(user.created_at).toLocaleDateString()}
            </p>
          </div>
        </div>
      </div>

      {/* Test History */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Test History</h2>
        
        {!user.is_active ? (
          <div className="text-center py-8">
            <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-4">
              <p className="font-medium">Account Deactivated</p>
              <p className="text-sm">Your account has been deactivated. Contact an administrator for assistance.</p>
            </div>
          </div>
        ) : loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : error ? (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        ) : testHistory.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-600 mb-4">You haven't taken any tests yet.</p>
            <a
              href="/test"
              className="inline-block bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700 transition-colors"
            >
              Take Your First Test
            </a>
          </div>
        ) : (
          <div className="space-y-4">
            {testHistory.map((test) => (
              <div
                key={test.id}
                className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h3 className="font-medium text-gray-900">
                      {test.test_type === 'placement' ? 'Placement Test' : test.test_type}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {new Date(test.completed_at).toLocaleDateString()} at{' '}
                      {new Date(test.completed_at).toLocaleTimeString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-semibold text-gray-900">
                      {test.score_percentage.toFixed(1)}%
                    </div>
                    <div className="text-sm text-gray-600">
                      {test.correct_answers}/{test.total_questions} correct
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span
                    className={`px-2 py-1 rounded text-sm font-medium ${getLevelColor(test.english_level)}`}
                  >
                    {test.english_level}
                  </span>
                  <a
                    href={`/results/${test.id}`}
                    className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                  >
                    View Details →
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;

